import React, { useRef, useEffect } from 'react'
import * as THREE from 'three'
import { logInfo } from '../../../utils/errorLogger'
import type { STLLoadResult } from '../../../utils/stlLoader'

interface STLMeshProps {
  stlData: STLLoadResult
  showWireframe: boolean
  calibrationMode: boolean
  onCalibrationPointClick: (point: { x: number; y: number; z: number }) => void
}

export const STLMesh: React.FC<STLMeshProps> = ({
  stlData,
  showWireframe,
  calibrationMode,
  onCalibrationPointClick
}) => {
  const meshRef = useRef<THREE.Mesh>(null)
  const wireframeRef = useRef<THREE.LineSegments>(null)

  useEffect(() => {
    if (meshRef.current && stlData) {
      meshRef.current.geometry = stlData.geometry
    }
  }, [stlData])

  // Handle click on mesh for calibration
  const handleMeshClick = (event: any) => {
    if (!calibrationMode) return

    event.stopPropagation()

    const point = event.point
    logInfo(`Mesh clicked at: (${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)})`, 'STL_IN')

    // Delegate point capture to parent
    onCalibrationPointClick({ x: point.x, y: point.y, z: point.z })
  }

  return (
    <group>
      {/* Main STL mesh */}
      <mesh
        ref={meshRef}
        geometry={stlData.geometry}
        castShadow
        receiveShadow
        onClick={handleMeshClick}
        onPointerOver={(e) => {
          if (calibrationMode) {
            e.stopPropagation()
            document.body.style.cursor = 'crosshair'
          }
        }}
        onPointerOut={(e) => {
          if (calibrationMode) {
            e.stopPropagation()
            document.body.style.cursor = 'default'
          }
        }}
      >
        <meshPhongMaterial
          color="#8B4513"
          shininess={30}
          flatShading={false}
          transparent
          opacity={showWireframe ? 0.7 : 0.9}
        />
      </mesh>

      {/* Wireframe overlay */}
      {showWireframe && (
        <lineSegments
          ref={wireframeRef}
          geometry={new THREE.WireframeGeometry(stlData.geometry)}
        >
          <lineBasicMaterial color="#000000" transparent opacity={0.3} />
        </lineSegments>
      )}
    </group>
  )
}
